import { createCancellationTokenSource } from 'infra/cancellation'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import once from 'lodash/once'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { share, switchMap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import JobCreationViewModel from './JobCreationViewModel'
import { JobCreationPorts } from './ports'

/**
 * Job Creation Root View Model.
 * Responsible for managing instances of Job Creation.
 */
export default class JobCreationRootViewModel {
  /**
   * Instances of editors.
   *
   * @private
   */
  private instances = new Map<Id, JobCreationViewModel>()

  /**
   * Cancellation token source.
   *
   * @private
   */
  private cancellationTokenSource = createCancellationTokenSource()

  /**
   * Current editor instance.
   */
  @observable current = this.createEditor()

  /**
   * Current editor instance as a stream.
   */
  current$ = toStream(() => this.current, true)

  /**
   * Emits a value when a Draft Job was created.
   */
  jobCreated$ = this.current$.pipe(
    switchMap((editor) => editor.jobCreated$),
    share(),
  )

  /**
   * Emits a value when a Draft Job was deleted.
   */
  notADraft$ = this.current$.pipe(
    switchMap((editor) => editor.notADraft$),
    share(),
  )

  /**
   * Emits a value when a Draft Job was deleted.
   */
  jobDeleted$ = this.current$.pipe(
    switchMap((editor) => editor.jobDeleted$),
    share(),
  )

  /**
   * Constructor.
   */
  constructor(private adapter: JobCreationPorts) {
    makeObservable(this)
  }

  /**
   * Whether the user can edit draft jobs.
   */
  canEditDraftJobAsAdmin(workspaceId: Id) {
    return this.adapter
      .getCurrentMember(workspaceId)
      .hasPermission('ADMIN_JOB_CREATE')
  }

  /**
   * Whether the current user is allowed to start a draft.
   *
   * Checks permissions for teams as well. It is a prerequisite that
   * all the necessary info has been fetched which is
   * done with `fetchAccessInfo`
   */
  @computed
  get canDraft() {
    const members = this.adapter.availableWorkspaceMembers()
    return members.some((m) => {
      return (
        m.hasSomePermissions('JOB_CREATE', 'ADMIN_JOB_CREATE') ||
        this.adapter
          .getAvailableTeamMembersByUserAndWorkspace(
            this.adapter.getCurrentMember().user.id,
            Id.assert(m.workspace.id),
          )
          .some((tm) =>
            tm.hasSomePermissions('CREATE_TEAM_JOBS', 'START_TEAM_JOBS'),
          )
      )
    })
  }

  /**
   * Whether we are allowed to submit the Draft that is currently loaded.
   */
  @computed
  get canSubmit() {
    return this.current.job.canSubmit
  }

  /**
   * Whether the current user is allowed to view the draft editor.
   */
  @computed
  get canViewEditor() {
    return this.canSubmit || this.canDraft
  }

  /**
   * Activates the root.
   */
  @action.bound
  activate() {
    // Nothing to do here, since `edit` activates the editor at the right time.
  }

  /**
   * Deactivates the root and cancels the current token. Any pending `edit` will
   * gracefully cancel.
   */
  @action.bound
  deactivate() {
    this.cancellationTokenSource.cancel()
    this.edit.reset()
    this.current.deactivate()
  }

  /**
   * Prepares the View Model with an existing draft, or by resetting
   * to prepare for a new draft.
   *
   * Will be called when:
   *   1. Loading an existing draft
   *   2. Starting a new draft
   *
   * Will NOT be called after a draft instance is created
   * in point 2 above, because at that point we already have that
   * draft's data loaded.
   *
   * @param jobId If specified, loads the Job
   * @param newDraft If creating a new draft, uses this context
   * @param isDisabled If specified, will be used to determine whether the fields from the editor are disabled
   */
  edit = task(
    async (
      jobId: Id,
      newDraft: PrefilledDraftInput,
      isDisabled?: () => boolean,
    ) => {
      // Start by fetching info required to determine whether we can view this screen.
      await this.fetchAccessInfo()

      // If we can't view the editor, exit early.
      if (!this.canViewEditor) {
        return this.current
      }

      // If the current editor is revivable, do so.
      if (this.current.isRevivable(jobId)) {
        // NOTE: No cancellation token is passed because
        // the previous one that was used for `prepare` is
        // used in case we are reviving a prepare that is running.
        await this.current.revive()
        return this.current
      }

      // Cancel any currently running edit/prepare tasks.
      this.cancellationTokenSource.cancel()
      this.cancellationTokenSource = createCancellationTokenSource()
      const ct = this.cancellationTokenSource.token

      if (Id.some(jobId)) {
        // Check for an existing editor or create a new one.
        const editor =
          this.instances.get(jobId) || this.createEditor(isDisabled)
        this.set(editor)
      } else if (this.current.jobWasCreated || !newDraft.isEmpty) {
        // In this case, the current editor represents an existing draft
        // and we are trying to start a new one, so replace it.
        // On the other hand, if we go from "new" to "new", we don't
        // want to reset the state by trashing the current editor unless
        // we are prefilling stuff.
        this.set(this.createEditor(isDisabled))
      }

      // Prepare the editor.
      await this.current.prepare(jobId, newDraft, ct)
      ct.throwIfCancelled()
      return this.current
    },
  )

  /**
   * Fetches info required for making access decisions.
   *
   * Wrapped in `once` since we only want to do this once per session.
   *
   * @private
   */
  private fetchAccessInfo = once(async () => {
    await Promise.all(
      this.adapter
        .availableWorkspaceMembers()
        .map((m) => this.adapter.fetchUserTeams(Id.assert(m.workspace.id))),
    )
  })

  /**
   * Sets the editor and activates it.
   *
   * @param editor
   * @private
   */
  @action.bound
  private set(editor: JobCreationViewModel) {
    if (editor === this.current) {
      return
    }

    // NOTE: We call deactivate here, but not activate,
    // since that gets called by `edit`.
    this.current.deactivate()
    if (!this.instances.has(this.current.job.id)) {
      // This editor is being completely trashed since we don't
      // have the job in the instance map.
      this.current.dispose()
    }
    this.current = editor
  }

  /**
   * Creates an editor.
   *
   * @private
   */
  private createEditor(isDisabled?: () => boolean): JobCreationViewModel {
    const editor = new JobCreationViewModel(
      this.adapter,
      (workspaceId) => this.canEditDraftJobAsAdmin(workspaceId),
      isDisabled ?? (() => false),
    )
    // Once we have a job loaded, save the editor instance in the instance map.
    editor.job$.subscribe((job) => {
      this.instances.set(job.id, editor)
    })
    // On dispose, remove the editor from the instance map.
    editor.disposed$.subscribe(() => {
      this.instances.delete(editor.job.id)
    })

    return editor
  }
}
